import type { TravelerId } from "../types";

/**
 * Trip passphrase sticky login. Not real auth — voter identity across tabs.
 * First-party cookie survives refresh and new tabs/windows (~30 days).
 * Vote tallies stay in localStorage.
 */
export const VOTER_COOKIE_NAME = "chile-trip-voter";

/** @deprecated Alias kept for any leftover references; cookie name is the same string. */
export const VOTER_STORAGE_KEY = VOTER_COOKIE_NAME;

const MAX_AGE_SECONDS = 30 * 24 * 60 * 60; // ~30 days

/**
 * `Mike` → michael, `Suzy` → suzy. Case-insensitive. Anything else is rejected.
 */
export function voterFromPassphrase(input: string): TravelerId | null {
  const normalized = input.trim().toLowerCase();
  if (normalized === "mike") return "michael";
  if (normalized === "suzy") return "suzy";
  return null;
}

function parseCookieValue(raw: string | undefined): TravelerId | null {
  if (raw === "michael" || raw === "suzy") return raw;
  return null;
}

function readCookie(name: string): string | undefined {
  try {
    if (typeof document === "undefined") return undefined;
    const parts = document.cookie.split(";");
    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const eq = trimmed.indexOf("=");
      const key = eq === -1 ? trimmed : trimmed.slice(0, eq);
      if (key === name) {
        return eq === -1 ? "" : decodeURIComponent(trimmed.slice(eq + 1));
      }
    }
  } catch {
    /* private mode / unavailable */
  }
  return undefined;
}

function isHttps(): boolean {
  try {
    return typeof location !== "undefined" && location.protocol === "https:";
  } catch {
    return false;
  }
}

function buildCookie(name: string, value: string, maxAge: number): string {
  const secure = isHttps() ? "; Secure" : "";
  return `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

export function readStoredVoter(): TravelerId | null {
  return parseCookieValue(readCookie(VOTER_COOKIE_NAME));
}

export function writeStoredVoter(id: TravelerId) {
  if (id !== "michael" && id !== "suzy") return;
  try {
    document.cookie = buildCookie(VOTER_COOKIE_NAME, id, MAX_AGE_SECONDS);
  } catch {
    /* still allow the in-memory session */
  }
}

export function clearStoredVoter() {
  try {
    // Expire immediately so Sign out lets Mike switch to Suzy.
    document.cookie = buildCookie(VOTER_COOKIE_NAME, "", 0);
  } catch {
    /* ignore */
  }
}
